import { IAcOverlayDesignImperial, StructuralEvaluationMethods } from '@/modules/design/interfaces/acOverlay';
import { aashto, findZr } from '@/modules/design/lib/calculations/calculationUtils';


export const calculateAcOverlay = function (scenario: IAcOverlayDesignImperial) {
  // console.log('starting ac overlay calcs');
  const reliability = scenario.reliabilityLevel ? scenario.reliabilityLevel : 0;
  const zr = findZr(reliability);

  const delta_psi = scenario.deltaServiceability ? Math.abs(scenario.deltaServiceability) : 0;
  const esal = scenario.totalDesignEsals ? scenario.totalDesignEsals : 0;
  const std_error = scenario.standardError ? scenario.standardError : 0;
  // const existingResilientModulus = data.existingResilientModulus ? data.existingResilientModulus : 0;
  // const designSubgradeModulus = scenario.designSubgradeModulus_psi ? scenario.designSubgradeModulus_psi : 0;
  const designSubgradeModulus = scenario.subgradeModulus_psi || 0;

  let existingResilientModulus = designSubgradeModulus;

  // TODO: Check on this calculation -> where should existingResilientModulus come from
  if (scenario.structuralEvaluationMethod === StructuralEvaluationMethods.testing && designSubgradeModulus > 0) {
    existingResilientModulus = designSubgradeModulus;
  }

  const existingLayerCoeff: number = scenario.existingLayerCoeff ? scenario.existingLayerCoeff : 0;
  const estimatedStructuralCoefficient: number = scenario.estimatedStructuralCoefficient ? scenario.estimatedStructuralCoefficient : 0;
  let totalSN: number = 0;
  let SNEffAfter: number = 0;
  let depthRemovedRemaining: number = scenario.removeDepth_in ? scenario.removeDepth_in : 0;
  let depth: number;

  // console.log('1');

  scenario.existingLayers.forEach((layer) => {
    if (layer.type !== 'Asphalt - Open Graded') {
      depth = (layer.thickness_in);
      if (layer.type === 'Asphalt - Dense Graded') {
        if (depthRemovedRemaining > 0) {
          if (depth >= depthRemovedRemaining) {
            depth -= depthRemovedRemaining;
            depthRemovedRemaining = 0;
          } else {
            depthRemovedRemaining -= depth;
            depth = 0;
          }
        }
      }

      const layerCoeff = layer.layerCoefficient || 0;

      const layerSN = layerCoeff * layer.drainageCoefficient * depth ? layerCoeff * layer.drainageCoefficient * depth : 0;
      SNEffAfter += layerSN;
      totalSN += layerCoeff * layer.drainageCoefficient * layer.thickness_in ? layerCoeff * layer.drainageCoefficient * layer.thickness_in : 0;
    }
  });

  // console.log('2');

  let snRemoved = 0;
  let asphaltLayerCoefficient = 0;

  if (scenario.structuralEvaluationMethod === StructuralEvaluationMethods.testing) {
    snRemoved = (scenario.removeDepth_in || 0) * (scenario.estimatedStructuralCoefficient || 0);
  } else if (scenario.existingLayers.length > 0) {
    snRemoved = scenario.existingLayers.map((obj) => {
      let removedDepth = scenario.removeDepth_in;
      if (obj.type === 'Asphalt - Open Graded' || obj.type === 'Asphalt - Dense Graded') {
        if (obj === scenario.existingLayers[0] && scenario.existingLayers[1] && scenario.existingLayers[1].type !== 'Asphalt - Dense Graded') {
          asphaltLayerCoefficient = obj.layerCoefficient || 0;
        } else if (obj === scenario.existingLayers[0] && scenario.existingLayers[1] && scenario.existingLayers[1].type === 'Asphalt - Dense Graded') {
          asphaltLayerCoefficient = scenario.existingLayers[1].layerCoefficient || 0;
        } else if (obj === scenario.existingLayers[0] && scenario.existingLayers[0].type === 'Asphalt - Dense Graded') {
          asphaltLayerCoefficient = obj.layerCoefficient || 0;
        }
        const thickness = obj.thickness_in || 0;

        if (thickness >= (removedDepth || 0)) {
          let sn = (removedDepth || 0 / thickness) * obj.sn;

          removedDepth = 0;

          sn = sn || 0;

          return sn;
        }
        // @ts-ignore
        removedDepth -= thickness;
        if (obj.sn && !isNaN((obj.sn))) {
          return (obj.sn);
        }
        return 0.0;
      }
      return 0;
    }).reduce((previousValue, currentValue, index, array) => previousValue + currentValue);
  }

  // console.log('3');

  // console.log(esal, zr, std_error, delta_psi, existingResilientModulus);

  const designSN = aashto(esal, zr, std_error, delta_psi, existingResilientModulus);

  // console.log('designSN', designSN);

  // console.log('3.1');

  const effectiveSN = scenario.structuralEvaluationMethod === StructuralEvaluationMethods.testing ? scenario.combinedLayerStiffness : totalSN;
  const neededSN = designSN - (effectiveSN || 0);

  const neededSNAfter = scenario.structuralEvaluationMethod === StructuralEvaluationMethods.testing ? (effectiveSN || 0) - snRemoved : SNEffAfter;
  const overlayLayerMinimumThickness = scenario.overlayLayerMinimumThickness_in ? scenario.overlayLayerMinimumThickness_in : 0;
  const overlaySN = (designSN - neededSNAfter);
  let neededDepth = overlaySN / existingLayerCoeff;

  if (overlayLayerMinimumThickness > neededDepth) {
    neededDepth = overlayLayerMinimumThickness;
  }
  if (neededDepth <= 0) {
    neededDepth = 0;
  }

  // console.log('Creating obj');

  const obj: any = {
    layers: [],
    layerCalculations: {
      overlay: {
        existingResilientModulus: existingResilientModulus.toFixed(0),
        designSN: designSN.toFixed(2),
        effectiveSN: (effectiveSN || 0).toFixed(2),
        removedDepth: scenario.removeDepth_in ? scenario.removeDepth_in : 0,
        snRemoved: snRemoved.toFixed(2),
        existingLayerCoeff: existingLayerCoeff.toFixed(2),
        estimatedStructuralCoefficient: estimatedStructuralCoefficient.toFixed(2),
        overlayLayerMinimumThickness: overlayLayerMinimumThickness.toFixed(2),
        neededDepth: neededDepth.toFixed(2),
        overlaySN: overlaySN.toFixed(2),
        effectiveSNAfter: neededSNAfter.toFixed(2),
        asphaltLayerCoefficient: asphaltLayerCoefficient ? asphaltLayerCoefficient.toFixed(2) : (0).toFixed(2),
      },
    },
    warning: '',
  };

  if (neededDepth <= 0) {
    obj.warning = 'A structural overlay is not required for this pavement based on the loading and conditions described for this project.';
    neededDepth = 0;
  }

  obj.layers.push({
    label: 'Overlay',
    depth: neededDepth.toFixed(1),
    type: 'asphalt',
    showLayer: neededDepth > 0,
  });

  depthRemovedRemaining = (scenario.removeDepth_in || 0);

  // console.log('Evaluating layers');

  scenario.existingLayers.forEach((layer) => {
    depth = (layer.thickness_in);
    if (layer.type === 'Asphalt - Open Graded') {
      depth = 0;
    }
    if (layer.type === 'Asphalt - Dense Graded') {
      if (depthRemovedRemaining > 0) {
        if (depth >= depthRemovedRemaining) {
          depth -= depthRemovedRemaining;
          depthRemovedRemaining = 0;
        } else {
          depthRemovedRemaining -= depth;
          depth = 0;
        }
      }
    }

    obj.layers.push({
      label: layer.type,
      depth,
      type: 'asphalt',
      showLayer: depth > 0,
    });
  });
  return obj;
};
